import { QUERY_KEYS } from "@/api/constants";
import { queryClient } from "@/api/queryClient";

import { brandsApi } from "./brandApi";
import type { BrandId, BrandMenuGet, BrandMenuPost } from "./types";

const EMPTY_MENU_POST: BrandMenuPost = {
  categories: [],
};

export const menuQueryKey = (brandId: BrandId) =>
  [QUERY_KEYS.brands, brandId, "menu"] as const;

export type MenuCacheEntry = {
  isLoaded: boolean;
  menu: BrandMenuGet | null;
};

export const getMenuCacheEntry = (brandId: BrandId): MenuCacheEntry => {
  const state = queryClient.getQueryState<BrandMenuGet | null>(
    menuQueryKey(brandId)
  );

  if (!state || state.status !== "success") {
    return { isLoaded: false, menu: null };
  }

  return { isLoaded: true, menu: state.data ?? null };
};

export const setMenuQueryData = (
  brandId: BrandId,
  menu: BrandMenuGet | null
): void => {
  queryClient.setQueryData(menuQueryKey(brandId), menu);
};

/**
 * Creates menu when init fetch confirmed it is missing.
 * Uses react-query cache after the first GET — no extra GET before PATCH/POST full.
 */
export const ensureMenuExists = async (
  brandId: BrandId
): Promise<BrandMenuGet> => {
  const { isLoaded, menu } = getMenuCacheEntry(brandId);

  if (isLoaded) {
    if (menu) {
      return menu;
    }

    const created = await brandsApi.createMenu(brandId, EMPTY_MENU_POST);
    setMenuQueryData(brandId, created);
    return created;
  }

  const fetched = await brandsApi.getMenu(brandId);

  if (fetched) {
    setMenuQueryData(brandId, fetched);
    return fetched;
  }

  const created = await brandsApi.createMenu(brandId, EMPTY_MENU_POST);
  setMenuQueryData(brandId, created);
  return created;
};
